import PageLoader from "@/components/loader/PageLoader";
import SmallSpinner from "@/components/loader/SmallSpinner";
import { useUser } from "@/contexts/UserContext";
import { useAxiosInstance } from "@/utils/useAxiosInstance";
import {
  faClipboardList,
  faDollarSign,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { startOfDay, endOfDay } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SidebarShrinkContext } from "../../Sidebar";

interface Order {
  id: number;
  customerName: string;
  created_at: string;
  total_price: string;
  cost: string | "0";
  status: string;
}

const Card = () => {
  const { t } = useTranslation();
  const { user } = useUser();
  const { shrinked } = useContext(SidebarShrinkContext);

  const [todaysIncome, setTodaysIncome] = useState<number>(0);
  const [incomeGrowthPercentage, setIncomeGrowthPercentage] =
    useState<number>(0);
  const [todaysOrders, setTodaysOrders] = useState<number>(0);
  const [orderGrowthPercentage, setOrderGrowthPercentage] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  const [axiosInstance, axiosLoading, isFetching] = useAxiosInstance();
  const selectedBranch = user?.selectedBranch?.id;
  const [branchTimezone, setBranchTimezone] = useState<string>("UTC");

  useEffect(() => {
    const initializeTimezone = () => {
      // First try to get timezone from user context
      if (user?.selectedBranch?.timezone) {
        setBranchTimezone(user.selectedBranch.timezone);
        setInitialized(true);
        return;
      }

      // Fallback to localStorage
      const storedData = localStorage.getItem("user_data");
      if (storedData) {
        const parsedData = JSON.parse(storedData);
        setBranchTimezone(parsedData?.selectedBranch?.timezone || "UTC");
        setInitialized(true);
      }
    };

    initializeTimezone();
  }, [user?.selectedBranch?.timezone]);

  const formatToUTC = useCallback(
    (date: Date, isEndOfDay: boolean = false): string => {
      // Convert to branch timezone first
      const zonedDate = toZonedTime(date, branchTimezone);

      // Get start or end of day in branch timezone
      const localDate = isEndOfDay
        ? endOfDay(zonedDate)
        : startOfDay(zonedDate);

      // Convert local branch time directly to UTC timestamp
      return new Date(
        localDate.toLocaleString("en-US", {
          timeZone: branchTimezone,
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        }),
      )
        .toISOString()
        .slice(0, 19);
    },
    [branchTimezone],
  );

  const fetchOrders = useCallback(async () => {
    if (axiosLoading || !axiosInstance || !selectedBranch || !initialized) {
      resetState();
      return;
    }

    const todayStart = formatToUTC(new Date());
    const todayEnd = formatToUTC(new Date(), true);
    const yesterdayStart = formatToUTC(new Date(Date.now() - 86400000));
    const yesterdayEnd = formatToUTC(new Date(Date.now() - 86400000), true);

    try {
      const [todayRes, yesterdayRes] = await Promise.all([
        axiosInstance.get(
          `api/v1/order/?branch=${selectedBranch}&created_at_after=${todayStart}&created_at_before=${todayEnd}&payment_status=Paid`,
        ),
        axiosInstance.get(
          `api/v1/order/?branch=${selectedBranch}&created_at_after=${yesterdayStart}&created_at_before=${yesterdayEnd}&payment_status=Paid`,
        ),
      ]);
      const [todayOrders, yesterdayOrders] = await Promise.all([
        fetchAllOrders(todayRes),
        fetchAllOrders(yesterdayRes),
      ]);
      processOrders(todayOrders, yesterdayOrders);
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
    }
  }, [
    axiosLoading,
    axiosInstance,
    selectedBranch,
    branchTimezone,
    initialized,
    formatToUTC,
  ]);

  const fetchAllOrders = async (response: any): Promise<Order[]> => {
    let allOrders: Order[] = response.data.results;
    const totalPages = Math.ceil(response.data.count / 10);

    for (let page = 2; page <= totalPages; page++) {
      const pageResponse = await axiosInstance.get(
        `${response.config.url}&page=${page}`,
      );
      allOrders = [...allOrders, ...pageResponse.data.results];
    }

    return allOrders;
  };

  const processOrders = (todayOrders: Order[], yesterdayOrders: Order[]) => {
    const todayIncome = todayOrders.reduce(
      (sum, order) => sum + parseFloat(order.total_price),
      0,
    );
    const yesterdayIncome = yesterdayOrders.reduce(
      (sum, order) => sum + parseFloat(order.total_price),
      0,
    );

    const todayOrderCount = todayOrders.length;
    const yesterdayOrderCount = yesterdayOrders.length;

    setTodaysIncome(todayIncome);
    setTodaysOrders(todayOrderCount);

    setIncomeGrowthPercentage(
      calcGrowthPercentage(todayIncome, yesterdayIncome),
    );
    setOrderGrowthPercentage(
      calcGrowthPercentage(todayOrderCount, yesterdayOrderCount),
    );
  };

  const calcGrowthPercentage = (today: number, yesterday: number): number => {
    if (yesterday === 0) {
      return today > 0 ? 100 : 0;
    }
    return ((today - yesterday) / yesterday) * 100;
  };

  const resetState = () => {
    setTodaysIncome(0);
    setIncomeGrowthPercentage(0);
    setTodaysOrders(0);
    setOrderGrowthPercentage(0);
    setLoading(false);
  };

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  return (
    <PageLoader isFetching={isFetching}>
      <div className="grid grid-cols-2 gap-2 sm:gap-6">
        <div className="min-h-[100px] rounded-lg bg-white p-2 shadow-md sm:min-h-[150px] sm:p-4">
          {loading ? (
            <div className="flex h-full items-center justify-center">
              <SmallSpinner />
            </div>
          ) : (
            <div className="flex items-center justify-between p-2 sm:p-3">
              <div>
                <p className="text-sm font-medium text-gray-600 sm:text-lg">
                  {t("back.management.dashboard.card.card1")}
                </p>
                <p className="text-xl font-bold sm:text-3xl">
                  {todaysIncome !== null
                    ? `$${todaysIncome.toFixed(2)}`
                    : "0.00"}
                </p>
                <p className="text-xs font-medium text-gray-600 sm:text-sm">
                  {incomeGrowthPercentage !== null
                    ? `${incomeGrowthPercentage.toFixed(2)}%`
                    : "0.00"}{" "}
                  {t("back.management.dashboard.fromyesterday")}
                </p>
              </div>
              <FontAwesomeIcon
                icon={faDollarSign}
                className="text-xl text-red-500 sm:text-3xl"
              />
            </div>
          )}
        </div>

        <div className="min-h-[100px] rounded-lg bg-white p-2 shadow-md sm:min-h-[150px] sm:p-4">
          {loading ? (
            <div className="flex h-full items-center justify-center">
              <SmallSpinner />
            </div>
          ) : (
            <div className="flex items-center justify-between p-2 sm:p-3">
              <div>
                <p className="text-sm font-medium text-gray-600 sm:text-lg">
                  {t("back.management.dashboard.card.card2")}
                </p>
                <p className="text-xl font-bold sm:text-3xl">
                  {todaysOrders !== null ? todaysOrders : "0"}
                </p>
                <p className="text-xs font-medium text-gray-600 sm:text-sm">
                  {orderGrowthPercentage !== null
                    ? `${orderGrowthPercentage.toFixed(2)}%`
                    : "0.00"}{" "}
                  {t("back.management.dashboard.fromyesterday")}
                </p>
              </div>
              <FontAwesomeIcon
                icon={faClipboardList}
                className="text-xl text-red-500 sm:text-3xl"
              />
            </div>
          )}
        </div>
      </div>
    </PageLoader>
  );
};

export default Card;
